import { supabase } from "./client";
import { logger } from "@/utils/logger";

export interface CustomSize {
  id: string;
  empresa_id: string;
  label: string;
  value: string;
  created_at: string;
  created_by?: string;
}

/**
 * Lista todos os tamanhos customizados de uma empresa
 */
export async function getCustomSizes(empresaId: string): Promise<CustomSize[]> {
  try {
    const { data, error } = await supabase
      .from("custom_clothing_sizes")
      .select("*")
      .eq("empresa_id", empresaId)
      .order("created_at", { ascending: true });

    if (error) {
      logger.error("Erro ao buscar tamanhos customizados:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    logger.error("Erro ao buscar tamanhos customizados:", error);
    return [];
  }
}

/**
 * Cria um novo tamanho customizado
 */
export async function createCustomSize(
  empresaId: string,
  label: string,
  value: string,
  userId?: string
): Promise<{ ok: boolean; data?: CustomSize; error?: string }> {
  try {
    // Validar entrada
    if (!label.trim() || !value.trim()) {
      return { ok: false, error: "Label e valor são obrigatórios" };
    }

    const { data, error } = await supabase
      .from("custom_clothing_sizes")
      .insert({
        empresa_id: empresaId,
        label: label.trim(),
        value: value.trim().toUpperCase(),
        created_by: userId,
      })
      .select()
      .single();

    if (error) {
      logger.error("Erro ao criar tamanho customizado:", error);
      // Verificar se é erro de duplicata
      if (error.code === "23505") {
        return { ok: false, error: `O tamanho "${value}" já existe` };
      }
      return { ok: false, error: error.message };
    }

    logger.debug(`✅ Tamanho customizado criado: ${label}`);
    return { ok: true, data };
  } catch (error) {
    logger.error("Erro ao criar tamanho customizado:", error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro desconhecido",
    };
  }
}

/**
 * Deleta um tamanho customizado
 */
export async function deleteCustomSize(
  customSizeId: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from("custom_clothing_sizes")
      .delete()
      .eq("id", customSizeId);

    if (error) {
      logger.error("Erro ao deletar tamanho customizado:", error);
      return { ok: false, error: error.message };
    }

    logger.debug(`✅ Tamanho customizado deletado: ${customSizeId}`);
    return { ok: true };
  } catch (error) {
    logger.error("Erro ao deletar tamanho customizado:", error);
    return {
      ok: false,
      error: error instanceof Error ? error.message : "Erro desconhecido",
    };
  }
}
